import React, { useState } from "react";

const ProductTable = ({
  toggleModal,
  currentProducts,
  indexOfFirstProduct,
  indexOfLastProduct,
  productsLength,
  totalPages,
  currentPage,
  handlePageChange,
  toggleDropdown,
  openDropdownId,
  handleEditModalOpen,
  handlePreviewModalOpen,
  handleDeleteModalOpen,
}) => {
  // State for tracking the search query
  const [searchQuery, setSearchQuery] = useState("");
  // State to track if any modal is open
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Filter the products based on the search query (by product name)
  const filteredProducts = currentProducts.filter((product) =>
    product.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Wrapper functions to handle modal open states
  const openEditModal = (product) => {
    setIsModalOpen(true);
    handleEditModalOpen(product);
  };

  const openPreviewModal = (product) => {
    setIsModalOpen(true);
    handlePreviewModalOpen(product);
  };

  const openDeleteModal = (product) => {
    setIsModalOpen(true);
    handleDeleteModalOpen(product);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  return (
    <section className="flex-grow bg-gray-50 dark:bg-gray-900 p-4 antialiased">
      <div className="mx-auto max-w-screen-xl px-4">
        <div className="bg-white dark:bg-gray-800 shadow-lg sm:rounded-lg overflow-hidden">
          {/* Search and Add Product Button */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-b dark:border-gray-700">
            <form className="w-full sm:w-1/2 relative">
              <label htmlFor="simple-search" className="sr-only">
                Search
              </label>
              <input
                type="text"
                id="simple-search"
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 p-2 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                placeholder="Search Products by Name"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <svg
                  className="w-5 h-5 text-gray-500 dark:text-gray-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M13.293 14.707a8 8 0 111.414-1.414l4.292 4.293-1.414 1.414-4.292-4.293zm-5.293 0a6 6 0 100-12 6 6 0 000 12z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </form>
            <button
              type="button"
              onClick={toggleModal}
              className="flex items-center gap-2 text-white bg-blue-600 hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-4 py-2 dark:bg-blue-600 dark:hover:bg-blue-700 focus:outline-none"
            >
              <i className="fa fa-plus"></i> Add Product
            </button>
          </div>

          {/* Products Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
              <thead className="text-xs uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                <tr>
                  <th className="px-4 py-3 hidden sm:table-cell">Image</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3 hidden md:table-cell">Category</th>
                  <th className="px-4 py-3 hidden lg:table-cell">
                    Subcategory
                  </th>
                  <th className="px-4 py-3 hidden lg:table-cell">Rating</th>
                  <th className="px-4 py-3 hidden md:table-cell">Discount</th>
                  <th className="px-4 py-3">Final Price</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.length > 0 ? (
                  filteredProducts.map((product) => (
                    <tr
                      key={product.id}
                      className="border-b dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800"
                    >
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <img
                          src={product.imageURI}
                          alt={product.name}
                          className="w-20 h-20 object-cover rounded-lg shadow-md hover:scale-105 transition-transform duration-300"
                        />
                      </td>
                      <td className="px-4 py-3">{product.name}</td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        {product.Category?.name || "N/A"}
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        {product.Subcategory?.name || "N/A"}
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        {product.average_rating}
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        {product.discount}%
                      </td>
                      <td className="px-4 py-3">
                        ${product.final_price || product.price}
                      </td>
                      <td className="px-4 py-3 relative">
                        <button
                          className="p-2 text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-white"
                          onClick={() => toggleDropdown(product.id)}
                        >
                          <i className="fa fa-ellipsis-h"></i>
                        </button>
                        {openDropdownId === product.id && !isModalOpen && (
                          <div className="absolute right-0 mt-2 w-40 bg-white dark:bg-gray-700 shadow-lg rounded-lg z-50">
                            <button
                              className="w-full px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center"
                              onClick={() => handleEditModalOpen(product)}
                            >
                              <i className="fas fa-edit mr-2"></i> Edit
                            </button>
                            <button
                              className="w-full px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center"
                              onClick={() => handlePreviewModalOpen(product)}
                            >
                              <i className="fas fa-eye mr-2"></i> Preview
                            </button>
                            <button
                              className="w-full px-4 py-2 text-sm text-red-500 hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center"
                              onClick={() => handleDeleteModalOpen(product)}
                            >
                              <i className="fas fa-trash mr-2"></i> Delete
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center py-4">
                      No products found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="p-4 flex flex-col sm:flex-row justify-between items-center border-t dark:border-gray-700">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Showing {indexOfFirstProduct + 1} to{" "}
              {Math.min(indexOfLastProduct, productsLength)} of {productsLength}{" "}
              Entries
            </span>
            <div className="flex space-x-2 mt-2 sm:mt-0">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                (pageNumber) => (
                  <button
                    key={pageNumber}
                    className={`px-3 py-1 rounded-lg ${
                      currentPage === pageNumber
                        ? "bg-blue-600 text-white"
                        : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-400 hover:bg-gray-300 dark:hover:bg-gray-600"
                    }`}
                    onClick={() => handlePageChange(pageNumber)}
                  >
                    {pageNumber}
                  </button>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductTable;
